import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut, Plus, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { resultsCopy } from "../i18n/results";
import { BRAND } from "../config/brand";
import { api, formatApiError } from "../lib/api";
import { LanguageSwitch } from "../components/LanguageSwitch";
import { ResultsTile } from "../components/BrandTiles";
import { Reveal } from "../components/Reveal";
import { LoginForm } from "../components/admin/LoginForm";
import { MediaCard, imageUrl } from "../components/results/MediaCard";
import { UploadDialog } from "../components/results/UploadDialog";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const TABS = ["all", "result", "testimonial"];
const dlg = "rounded-[28px] border-white/10 bg-surface text-zinc-100";

export default function Results() {
  const { lang } = useLang();
  const c = resultsCopy[lang];
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("all");
  const [me, setMe] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/media").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, [load]);

  const onAdd = () => (me ? setUploadOpen(true) : setLoginOpen(true));
  const onLogin = (user) => { setMe(user); setLoginOpen(false); setUploadOpen(true); };
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); setMe(null); };
  const canDelete = (item) => !!me && (me.role === "owner" || item.created_by === me.id);

  const confirmDelete = async () => {
    try {
      await api.delete(`/media/${toDelete.id}`);
      toast.success(c.del.done);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setToDelete(null);
  };

  const shown = tab === "all" ? items : items.filter((i) => i.kind === tab);

  return (
    <div className="min-h-[100svh] pb-32" data-testid="results-page">
      <header className="sticky top-0 z-50 border-b border-white/5 bg-ink/80 backdrop-blur-xl" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-5 sm:px-8">
          <Link to="/" data-testid="results-back-link" className="flex items-center gap-2 text-sm text-zinc-300 transition-colors hover:text-gold">
            <ArrowLeft className="h-4 w-4" />
            <img src={BRAND.logoSmall} alt="" className="h-7 w-7" />
            <span className="hidden sm:inline">{c.back}</span>
          </Link>
          <div className="flex items-center gap-2">
            {me && (
              <button onClick={logout} data-testid="results-logout" className="flex h-10 items-center gap-1.5 rounded-full border border-white/10 px-3 text-xs text-zinc-300 transition-colors hover:text-gold">
                <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{c.logout}</span>
              </button>
            )}
            <LanguageSwitch />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 pt-12 sm:px-8 sm:pt-16">
        <Reveal>
          <div className="flex items-center gap-4">
            <ResultsTile />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400">{c.kicker}</span>
          </div>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-zinc-50 sm:text-5xl lg:text-6xl" data-testid="results-title">
            {c.title[0]} <span className="font-serif font-normal italic text-gold">{c.title[1]}</span>
          </h1>
          <p className="mt-4 max-w-xl text-base text-zinc-300">{c.sub}</p>
          <p className="mt-5 flex max-w-xl items-start gap-2 rounded-2xl border border-white/[0.07] bg-white/[0.03] p-3 text-[12px] leading-relaxed text-zinc-400" data-testid="results-risk-note">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" /> {c.note}
          </p>
        </Reveal>

        <div className="mt-10 inline-flex rounded-full border border-white/10 bg-white/[0.04] p-1" data-testid="results-tabs">
          {TABS.map((k) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`results-tab-${k}`}
              className={`h-10 rounded-full px-4 text-sm font-semibold transition-colors sm:px-5 ${tab === k ? "bg-gold text-ink" : "text-zinc-400 hover:text-zinc-100"}`}>
              {c.tabs[k]}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {shown.length ? (
            <div className="columns-2 gap-3 sm:gap-4 lg:columns-3 xl:columns-4" data-testid="results-grid">
              {shown.map((item, i) => (
                <MediaCard key={item.id} item={item} index={i} copy={c} canDelete={canDelete(item)} onOpen={setViewing} onDelete={setToDelete} />
              ))}
            </div>
          ) : (
            <div className="rounded-[24px] border border-dashed border-white/10 py-20 text-center text-sm text-zinc-400" data-testid="results-empty">
              {loaded ? c.empty : "…"}
            </div>
          )}
        </div>
      </main>

      <button onClick={onAdd} data-testid="results-add-button" aria-label={c.add}
        className="fixed right-5 z-40 flex h-14 items-center gap-2 rounded-full bg-gold pl-4 pr-5 font-semibold text-ink shadow-[0_18px_50px_-12px_rgba(212,175,55,0.65)] transition-transform hover:scale-105 active:scale-95"
        style={{ bottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
        <Plus className="h-6 w-6" strokeWidth={2.5} /> {c.add}
      </button>

      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent data-testid="results-login-dialog" data-lenis-prevent className={`max-w-sm ${dlg}`}>
          <DialogDescription className="sr-only">{c.login.sub}</DialogDescription>
          <LoginForm onLogin={onLogin} submitLabel={c.login.submit} testId="results-login">
            <DialogTitle className="font-display text-xl">{c.login.title}</DialogTitle>
            <p className="mt-1 text-sm text-zinc-400">{c.login.sub}</p>
          </LoginForm>
        </DialogContent>
      </Dialog>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} copy={c} defaultKind={tab === "testimonial" ? "testimonial" : "result"} onUploaded={load} />

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent data-testid="results-lightbox" data-lenis-prevent className={`max-w-3xl p-3 sm:p-4 ${dlg}`}>
          <DialogTitle className="sr-only">{viewing?.caption || c.title.join(" ")}</DialogTitle>
          <DialogDescription className="sr-only">{c.sub}</DialogDescription>
          {viewing && <img src={imageUrl(viewing.id)} alt={viewing.caption} className="max-h-[78svh] w-full rounded-2xl object-contain" />}
          {(viewing?.caption || viewing?.author) && (
            <p className="px-1 pb-1 text-sm text-zinc-200">{viewing.caption}{viewing.author && <span className="text-zinc-400"> — {viewing.author}</span>}</p>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="results-delete-dialog" className={dlg}>
          <AlertDialogTitle className="font-display">{c.del.title}</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400">{c.del.sub}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="results-delete-cancel" className="rounded-full border-white/10 bg-transparent text-zinc-200 hover:bg-white/5">{c.del.cancel}</AlertDialogCancel>
            <AlertDialogAction data-testid="results-delete-confirm" onClick={confirmDelete} className="rounded-full bg-red-500 text-white hover:bg-red-500/90">{c.del.ok}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
